import React, { useEffect, useState } from "react";
import axios from "axios";
import "./championshipWait.css";

const pad = (value) => String(value).padStart(2, "0");

const formatLeft = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
};

const formatShort = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${pad(totalSeconds % 60)}`;
};

// Sobre el tablero de una partida del Campeonato: mientras falta el rival o la
// hora, muestra la cuenta regresiva. Se oculta en cuanto la partida empieza.
const ChampionshipWaitBanner = ({ tournamentId }) => {
  const [now, setNow] = useState(() => Date.now());
  // Hasta saber cómo está el partido no se muestra nada (evita un aviso equivocado
  // al que llega cuando el rival ya espera y la partida arranca al instante).
  const [state, setState] = useState({ checked: false, started: false, finished: false });
  const [room, setRoom] = useState({ matchId: "", startAt: null, timeMinutes: 5 });
  const { matchId, startAt, timeMinutes } = room;

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  // ¿Es una sala del Campeonato? Su hora puede cambiar, así que se vuelve a mirar.
  useEffect(() => {
    if (!tournamentId) return undefined;
    let canceled = false;
    let poll = null;
    const loadRoom = async () => {
      try {
        const response = await axios.get(`/tournaments/${tournamentId}`);
        const data = response?.data || {};
        if (canceled) return;
        // Partidas normales: no hay cartel ni más consultas.
        if (!data.championshipMatchId) {
          window.clearInterval(poll);
          return;
        }
        setRoom({
          matchId: String(data.championshipMatchId),
          startAt: data.championshipStartAt || null,
          timeMinutes: Number(data.havingTime) || 5,
        });
      } catch {
        // not a championship room, or not reachable: no banner
      }
    };
    poll = window.setInterval(loadRoom, 30000);
    loadRoom();
    return () => {
      canceled = true;
      window.clearInterval(poll);
    };
  }, [tournamentId]);

  useEffect(() => {
    if (!matchId) return undefined;
    let canceled = false;
    const check = async () => {
      try {
        const response = await axios.get("/championship");
        const match = (response?.data?.rounds || [])
          .flatMap((round) => round.matches || [])
          .find((entry) => entry.id === matchId);
        if (canceled || !match) return;
        setState({
          checked: true,
          started: Boolean(match.live) || String(match.tournamentId || "") !== String(tournamentId),
          finished: Boolean(match.winner),
        });
      } catch {
        // keep the banner as it is
      }
    };
    check();
    const poll = window.setInterval(check, 4000);
    const socket = window.__ximaSocket || window.socket || null;
    const handleStart = () => setState((prev) => ({ ...prev, started: true }));
    socket?.on?.("GAME_START", handleStart);
    return () => {
      canceled = true;
      window.clearInterval(poll);
      socket?.off?.("GAME_START", handleStart);
    };
  }, [matchId, tournamentId]);

  if (!matchId || !state.checked || state.started || state.finished) return null;
  const startMs = startAt ? new Date(startAt).getTime() : 0;
  const graceMs = Math.max(1, Number(timeMinutes) || 5) * 60 * 1000;

  let title = "Esperando al otro jugador";
  let detail = "El administrador todavía no ha puesto la hora del partido.";
  if (startMs && now < startMs) {
    detail = `El partido comienza en ${formatLeft(startMs - now)}`;
  } else if (startMs && now < startMs + graceMs) {
    detail = `Si el otro jugador no se presenta en ${formatShort(startMs + graceMs - now)}, ganarás la partida.`;
  } else if (startMs) {
    title = "Se acabó el tiempo de espera";
    detail = "Calculando el resultado…";
  }

  return (
    <div className="xc-wait-banner" role="status">
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
};

export default ChampionshipWaitBanner;
